import { useMemo } from 'react';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import { geoAlbersUsa } from 'd3-geo';
import { motion } from 'framer-motion';
import { MapPin } from 'lucide-react';

// Reliable US TopoJSON hosted on jsDelivr
const GEO_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

// Key states to spotlight on the map (lng, lat)
const HUBS = [
  { id: 'CA', name: 'California',    coords: [-119.4179, 36.7783] },
  { id: 'TX', name: 'Texas',         coords: [ -99.9018, 31.9686] },
  { id: 'FL', name: 'Florida',       coords: [ -81.5158, 27.6648] },
  { id: 'NY', name: 'New York',      coords: [ -75.4652, 42.9538] },
  { id: 'IL', name: 'Illinois',      coords: [ -89.3985, 40.6331] },
  { id: 'GA', name: 'Georgia',       coords: [ -83.6431, 32.6415] },
  { id: 'CO', name: 'Colorado',      coords: [-105.7821, 39.5501] },
  { id: 'WA', name: 'Washington',    coords: [-120.7401, 47.7511] },
  { id: 'PA', name: 'Pennsylvania',  coords: [ -77.1945, 41.2033] },
  { id: 'AZ', name: 'Arizona',       coords: [-111.0937, 34.0489] },
];

// Signal paths (from -> to). Randomized delays produce a live "network" feel.
const SIGNALS = [
  { from: 'CA', to: 'TX', delay: 0.0 },
  { from: 'TX', to: 'FL', delay: 0.8 },
  { from: 'FL', to: 'NY', delay: 1.6 },
  { from: 'NY', to: 'IL', delay: 2.4 },
  { from: 'IL', to: 'CO', delay: 3.2 },
  { from: 'CO', to: 'WA', delay: 4.0 },
  { from: 'WA', to: 'AZ', delay: 4.8 },
  { from: 'AZ', to: 'GA', delay: 5.6 },
  { from: 'GA', to: 'PA', delay: 6.4 },
  { from: 'PA', to: 'CA', delay: 7.2 },
];

const MAP_WIDTH = 980;
const MAP_HEIGHT = 560;

export const USMap = () => {
  // Build the same projection the map uses so we can compute [x, y] for signal lines
  const projection = useMemo(
    () => geoAlbersUsa().translate([MAP_WIDTH / 2, MAP_HEIGHT / 2]).scale(1200),
    []
  );

  const hubXY = useMemo(() => {
    const map = {};
    HUBS.forEach((h) => {
      const p = projection(h.coords);
      if (p) map[h.id] = p;
    });
    return map;
  }, [projection]);

  return (
    <section
      className="relative py-20 lg:py-28 overflow-hidden z-10"
      data-testid="us-map-section"
    >
      {/* ambient glows */}
      <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] bg-[#31638C] opacity-[0.08] rounded-full blur-3xl animate-float-slow pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-[#4A8BC2] opacity-[0.06] rounded-full blur-3xl animate-drift pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          className="text-center mb-12 lg:mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="text-xs uppercase tracking-[0.28em] text-[#31638C] font-semibold">
            NATIONWIDE COVERAGE
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold tracking-tight mt-4 text-white">
            Live Leads. <span className="gradient-text">50 States.</span>
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto mt-4">
            Real-time signals firing across the country the moment a buyer shows intent.
          </p>
        </motion.div>

        <motion.div
          className="relative rounded-3xl border border-[#5D676E]/25 bg-[#0A1420]/60 backdrop-blur-sm p-4 lg:p-8 shadow-2xl shadow-[#31638C]/10"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          {/* subtle grid pattern behind the map */}
          <div className="absolute inset-4 lg:inset-8 rounded-2xl grid-pattern opacity-30 pointer-events-none" />

          <div className="relative">
            <ComposableMap
              projection="geoAlbersUsa"
              projectionConfig={{ scale: 1200 }}
              width={MAP_WIDTH}
              height={MAP_HEIGHT}
              style={{ width: '100%', height: 'auto' }}
              data-testid="us-map-svg"
            >
              {/* Glow filter for signal pulses */}
              <defs>
                <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                  <feMerge>
                    <feMergeNode in="coloredBlur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
                <linearGradient id="signalGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#4A8BC2" stopOpacity="0" />
                  <stop offset="50%" stopColor="#7BBFFF" stopOpacity="0.9" />
                  <stop offset="100%" stopColor="#4A8BC2" stopOpacity="0" />
                </linearGradient>
              </defs>

              {/* State polygons */}
              <Geographies geography={GEO_URL}>
                {({ geographies }) =>
                  geographies.map((geo) => (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      style={{
                        default: {
                          fill: '#131E2C',
                          stroke: '#31638C',
                          strokeWidth: 0.6,
                          strokeOpacity: 0.45,
                          outline: 'none',
                        },
                        hover: {
                          fill: '#1A2A3F',
                          stroke: '#4A8BC2',
                          strokeWidth: 0.9,
                          strokeOpacity: 1,
                          outline: 'none',
                          cursor: 'pointer',
                        },
                        pressed: { fill: '#1A2A3F', outline: 'none' },
                      }}
                    />
                  ))
                }
              </Geographies>

              {/* Static connection lines (faint) */}
              {SIGNALS.map((sig, i) => {
                const a = hubXY[sig.from];
                const b = hubXY[sig.to];
                if (!a || !b) return null;
                return (
                  <line
                    key={`bg-${i}`}
                    x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]}
                    stroke="#31638C" strokeOpacity="0.18" strokeWidth="1"
                    strokeDasharray="4 6"
                  />
                );
              })}

              {/* Animated signal beams (dashed segment that traverses the line) */}
              {SIGNALS.map((sig, i) => {
                const a = hubXY[sig.from];
                const b = hubXY[sig.to];
                if (!a || !b) return null;
                const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
                return (
                  <g key={`sig-${i}`}>
                    <line
                      x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]}
                      stroke="url(#signalGrad)"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeDasharray={`${len * 0.18} ${len}`}
                      filter="url(#glow)"
                      opacity="0"
                    >
                      <animate
                        attributeName="stroke-dashoffset"
                        from={len}
                        to={-len * 0.18}
                        dur="2.4s"
                        begin={`${sig.delay}s`}
                        repeatCount="indefinite"
                      />
                      <animate
                        attributeName="opacity"
                        values="0;1;1;0"
                        keyTimes="0;0.1;0.85;1"
                        dur="2.4s"
                        begin={`${sig.delay}s`}
                        repeatCount="indefinite"
                      />
                    </line>
                  </g>
                );
              })}

              {/* Hub markers + labels */}
              {HUBS.map((h) => (
                <Marker key={h.id} coordinates={h.coords}>
                  {/* Outer pulsing ring */}
                  <circle r={6} fill="#4A8BC2" fillOpacity="0.22">
                    <animate
                      attributeName="r"
                      values="6;14;6"
                      dur="2.6s"
                      repeatCount="indefinite"
                    />
                    <animate
                      attributeName="fill-opacity"
                      values="0.35;0;0.35"
                      dur="2.6s"
                      repeatCount="indefinite"
                    />
                  </circle>
                  {/* Solid dot */}
                  <circle
                    r={3.2}
                    fill="#7BBFFF"
                    stroke="#0A1420"
                    strokeWidth="1"
                    filter="url(#glow)"
                  />
                  {/* Label */}
                  <text
                    y={-12}
                    textAnchor="middle"
                    style={{
                      fontFamily: "'Nexa','Nexa Pro',sans-serif",
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: '0.08em',
                      fill: '#E5E7EB',
                      paintOrder: 'stroke',
                      stroke: '#0A1420',
                      strokeWidth: 3,
                      strokeLinejoin: 'round',
                    }}
                  >
                    {h.name.toUpperCase()}
                  </text>
                </Marker>
              ))}
            </ComposableMap>

            {/* Legend */}
            <div className="mt-6 flex flex-wrap items-center justify-center gap-6 text-xs text-gray-400">
              <span className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-[#7BBFFF] shadow-[0_0_8px_#7BBFFF]" />
                Active hub
              </span>
              <span className="flex items-center gap-2">
                <span className="inline-block w-7 h-[2px] bg-gradient-to-r from-transparent via-[#7BBFFF] to-transparent" />
                Live signal
              </span>
              <span className="flex items-center gap-2 text-gray-500">
                <MapPin size={12} className="text-[#4A8BC2]" />
                Serving all 50 states
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default USMap;
